/**
 * The Super Admin's own account: who they are and how it is secured.
 *
 * Separate from the merchant Settings page on purpose — nothing here concerns a
 * store, a team or a merchant. Account details are read-only because the API
 * has no endpoint that edits them; a form that looks editable and is not would
 * be worse than saying so.
 */

import { useState } from 'react'
import { useAuth } from '../../hooks/useAuth.js'
import { initials } from '../../lib/format.js'
import { Badge, Card, KeyValue, Notice, PageHead, SettingRow } from '../../components/ui.js'
import { ChangePasswordCard } from '../../components/ChangePasswordCard.js'
import { TwoFactorCard } from '../../components/TwoFactorCard.js'

export function AdminProfilePage(): React.ReactNode {
  const { user } = useAuth()
  const name = user?.name ?? 'Account'

  return (
    <>
      <PageHead title="Profile" subtitle="Your administrator account and how it is secured." />

      <div className="grid grid--2">
        <div className="stack">
          <Card title="Account">
            <div className="row" style={{ marginBottom: 20 }}>
              <span className="avatar avatar--lg">{initials(name)}</span>
              <span className="list__text">
                <strong>{name}</strong>
                <small>{user?.email ?? ''}</small>
              </span>
            </div>

            <KeyValue
              rows={[
                { label: 'Role', value: <Badge tone="accent">Super Admin</Badge> },
                { label: 'Email', value: user?.email ?? '—' },
                { label: 'Phone', value: user?.phone ?? '—' },
              ]}
            />
          </Card>

          <SessionsCard />
        </div>

        <div className="stack">
          <ChangePasswordCard />
          <TwoFactorCard />
        </div>
      </div>
    </>
  )
}

function SessionsCard(): React.ReactNode {
  const { signOutEverywhere } = useAuth()
  const [failed, setFailed] = useState<string | null>(null)

  return (
    <Card title="Sessions">
      <SettingRow title="Sign out everywhere" description="Revokes every session on every device, including this one.">
        <button
          type="button"
          className="btn btn--danger btn--sm"
          onClick={() => {
            setFailed(null)
            signOutEverywhere().catch((error: Error) => setFailed(error.message))
          }}
        >
          Sign out everywhere
        </button>
      </SettingRow>
      {failed && <Notice kind="error">{failed}</Notice>}
    </Card>
  )
}
